import React, { useEffect, useRef, useState } from "react";
import { SFDC_ENTITIES } from "@amzn/afxtaco-types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { extractSfdcId } from "@amzn/afxtaco-utilities";
import {
  CustomerContextInput,
  validateCustomerContext,
} from "@/utils/customerContext";

interface LinkAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Called with the validated IDs once the user submits. Returns whether the save
   * succeeded: the dialog stays open on failure so `error` is actually reachable
   * and the user does not lose what they typed.
   */
  onSubmit: (input: CustomerContextInput) => boolean | Promise<boolean>;
  /** Save failure to surface, if the last attempt failed. */
  error?: string;
  /**
   * The account ID currently recorded on the project, if any. The form opens on
   * this value so editing means correcting what is there, not retyping it.
   */
  currentAccountId?: string;
  /** The opportunity ID currently recorded on the project, if any. */
  currentOpportunityId?: string;
}

const EMPTY: CustomerContextInput = { accountId: "", opportunityId: "" };

/**
 * Sets the Salesforce account and/or opportunity ID a project is grounded in.
 *
 * These are the same two fields the homepage offers when starting a project —
 * this is how you supply them afterwards, which was previously only possible by
 * typing them into chat and hoping the agent picked them up.
 *
 * Doubles as the edit path: a project has one customer, so reopening this with
 * IDs already recorded is how you fix a typo or repoint the project, and
 * submitting replaces what was there. Hence the adaptive copy below — the same
 * blank form under a "Change" button would read as adding a second customer.
 *
 * A dialog rather than inline fields because the status-strip box is ~236px
 * wide; two labelled inputs plus validation don't fit legibly there.
 */
const LinkAccountDialog: React.FC<LinkAccountDialogProps> = ({
  open,
  onOpenChange,
  onSubmit,
  error,
  currentAccountId = "",
  currentOpportunityId = "",
}) => {
  const [fields, setFields] = useState<CustomerContextInput>(EMPTY);
  const [showErrors, setShowErrors] = useState(false);
  // Both inputs submit on Enter and the button is clickable, so without this a
  // second submit before the first resolves fires a duplicate write.
  const [isSaving, setIsSaving] = useState(false);

  const isEditing = Boolean(currentAccountId || currentOpportunityId);

  // Read through a ref so the seeding effect below can depend on `open` alone.
  // Depending on the IDs directly would re-seed mid-edit — the project record
  // can change under us while the dialog is open (the agent writes this same
  // field), and that would silently discard what the user had typed.
  const currentRef = useRef<CustomerContextInput>(EMPTY);
  currentRef.current = {
    accountId: currentAccountId,
    opportunityId: currentOpportunityId,
  };

  // Seed once per opening rather than on mount: on a fresh page load the project
  // record usually hasn't arrived yet, so mount-time state would be blank and
  // stay blank.
  useEffect(() => {
    if (!open) return;
    setFields(currentRef.current);
    setShowErrors(false);
  }, [open]);

  const errors = validateCustomerContext(fields);
  const hasAnyValue = Boolean(
    fields.accountId.trim() || fields.opportunityId.trim(),
  );
  const canSubmit = hasAnyValue && Object.keys(errors).length === 0;

  // Accepts a pasted Salesforce Lightning URL as well as a bare ID — see
  // extractSfdcId. `fields` always ends up holding the clean ID.
  const set = (key: keyof CustomerContextInput, value: string) =>
    setFields((prev) => ({ ...prev, [key]: extractSfdcId(value) }));

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setFields(EMPTY);
      setShowErrors(false);
    }
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    if (isSaving) return;
    if (!canSubmit) {
      setShowErrors(true);
      return;
    }
    setIsSaving(true);
    try {
      const saved = await onSubmit(fields);
      if (saved) handleOpenChange(false);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Update customer" : "Link a customer"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "This replaces what the project is currently grounded in. Work TACO has already produced for the previous customer isn't revised automatically — ask it to revisit anything that should change."
              : "TACO will pull the account's footprint and open opportunities, and use them to shape the design. Either field is enough."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-1.5">
            <Label htmlFor="link-account-id">Salesforce Account ID / URL</Label>
            <Input
              id="link-account-id"
              value={fields.accountId}
              onChange={(e) => set("accountId", e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSubmit();
              }}
              placeholder={`e.g. ${SFDC_ENTITIES.ACCOUNT.prefix}00000001234WXYZ`}
              aria-invalid={showErrors && Boolean(errors.accountId)}
              autoFocus
            />
            {showErrors && errors.accountId && (
              <p className="text-xs text-red-600 dark:text-red-400">
                {errors.accountId}
              </p>
            )}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="link-opportunity-id">
              Salesforce Opportunity ID / URL
            </Label>
            <Input
              id="link-opportunity-id"
              value={fields.opportunityId}
              onChange={(e) => set("opportunityId", e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSubmit();
              }}
              placeholder={`e.g. ${SFDC_ENTITIES.OPPORTUNITY.prefix}ABC00001234WXYZ`}
              aria-invalid={showErrors && Boolean(errors.opportunityId)}
            />
            {showErrors && errors.opportunityId && (
              <p className="text-xs text-red-600 dark:text-red-400">
                {errors.opportunityId}
              </p>
            )}
          </div>

          {error ? (
            <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Saved to the project — TACO will use it from your next message on.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!hasAnyValue || isSaving}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LinkAccountDialog;
